// 📄 pages/Ariba/sections/InterfaceCatalogToolbox.tsx
// Acceso directo (standalone, sin proyectoId) al Catálogo de Interfaces/
// Canales desde el Ariba Toolbox — mismo catálogo global de solo lectura
// que /proyecto/:projectId/interfaces (ver [[InterfacesCatalogo.tsx]]),
// pero accesible sin estar dentro de un proyecto puntual. Mismo patrón que
// [[TransactionCatalogToolbox.tsx]].
import { useEffect, useRef, useState } from 'react';
import { RefreshCw, ArrowLeftRight, Info } from 'lucide-react';
import Breadcrumbs from '../../../components/ui/Breadcrumbs.tsx';
import { InterfaceChannel, getInterfaces, seedCatalogoInterfaces } from '../../../services/catalogoInterfacesService.ts';
import InterfaceCatalogGrid from '../../../components/interfaces/InterfaceCatalogGrid.tsx';
import { useAuth } from '../../../context/AuthContext.tsx';

interface InterfaceCatalogToolboxProps {
  setPage?: (page: string) => void;
}

export default function InterfaceCatalogToolbox({ setPage }: InterfaceCatalogToolboxProps) {
  const { user, profile } = useAuth();
  const esAdmin = profile?.role === 'admin';
  const [interfaces, setInterfaces] = useState<InterfaceChannel[]>([]);
  const [loading, setLoading] = useState(true);
  const [sembrando, setSembrando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const seedIntentado = useRef(false);

  useEffect(() => {
    (async () => {
      try {
        let data = await getInterfaces();
        if (data.length === 0 && esAdmin && !seedIntentado.current) {
          seedIntentado.current = true;
          setSembrando(true);
          try {
            await seedCatalogoInterfaces(user?.email || user?.uid || '');
            data = await getInterfaces();
          } finally {
            setSembrando(false);
          }
        }
        setInterfaces(data.filter((i) => i.activo));
      } catch (err) {
        console.error('Error cargando el catálogo de interfaces/canales:', err);
        setError('No se pudo cargar el catálogo de interfaces/canales.');
      } finally {
        setLoading(false);
      }
    })();
  }, [esAdmin, user]);

  return (
    <main className={setPage ? 'min-h-screen p-4 md:p-8 text-white' : 'text-white'}>
      {setPage && (
        <div className="max-w-6xl mx-auto mb-6">
          <Breadcrumbs currentPage="interfaces-toolbox" setPage={setPage} />
        </div>
      )}

      <div className="max-w-6xl mx-auto space-y-6">
        <header>
          <h1 className="text-2xl font-light text-white">Catálogo de Interfaces / Canales</h1>
          <p className="text-sm text-gray-400 mt-1">Interfaces de integración SAP Ariba ↔ ERP ↔ S/4HANA ↔ BTP: por dónde entra/sale cada flujo y en qué transacción se monitorea.</p>
        </header>

        <div className="bg-blue-500/10 border border-blue-500/20 text-blue-300 p-3 rounded-lg text-xs flex items-start gap-2">
          <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>Catálogo global de referencia técnica, mantenido desde el panel de Administración. Filtra por Solución, Plataforma o Dirección y usa el botón de copiar para llevarte el código de transacción directo al portapapeles.</span>
        </div>

        {error && (
          <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-lg text-xs">{error}</div>
        )}

        {loading ? (
          <div className="p-10 text-center text-gray-400 flex flex-col items-center gap-3">
            <RefreshCw className="w-6 h-6 animate-spin text-blue-500" />
            <p className="text-sm">{sembrando ? 'Inicializando catálogo con datos de ejemplo...' : 'Cargando catálogo...'}</p>
          </div>
        ) : interfaces.length === 0 ? (
          <div className="p-10 text-center text-gray-500 bg-white/5 border border-white/10 rounded-2xl">
            <ArrowLeftRight className="w-8 h-8 mx-auto mb-2 text-gray-600" />
            <p className="text-sm">Todavía no hay interfaces/canales registrados en el catálogo. Un Admin puede cargarlas desde /admin.</p>
          </div>
        ) : (
          <InterfaceCatalogGrid
            interfaces={interfaces}
            readOnly
            onVerEnTransacciones={setPage ? () => setPage('transacciones-toolbox') : undefined}
          />
        )}
      </div>
    </main>
  );
}
